import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authAxios = axios.create({ baseURL: API, withCredentials: true });

let refreshPromise = null;
authAxios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retried && !original.url.includes("/auth/refresh") && !original.url.includes("/auth/login")) {
      original._retried = true;
      try {
        refreshPromise = refreshPromise || authAxios.post("/auth/refresh").finally(() => { refreshPromise = null; });
        await refreshPromise;
        return authAxios(original);
      } catch (refreshErr) {
        console.error("Auth refresh failed:", refreshErr?.response?.status || refreshErr?.message);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  useEffect(() => {
    authAxios.get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await authAxios.post("/auth/login", { email, password });
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const r = await authAxios.post("/auth/register", { name, email, password, origin_url: window.location.origin });
    setUser(r.data.user);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    await authAxios.post("/auth/logout").catch((err) => console.error("Logout error:", err));
    setUser(false);
  }, []);

  const refreshUser = useCallback(
    () => authAxios.get("/auth/me").then((r) => setUser(r.data.user)).catch((err) => console.error("refreshUser error:", err)),
    []
  );

  const value = useMemo(
    () => ({ user, login, register, logout, refreshUser }),
    [user, login, register, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
